import { FC, useState } from "react";
import { PaperEditBillInfoProps } from "../types/PaperEditBillInfo.types";
import { usePaperBillEditInfo } from "../hooks/usePaperBillEditInfo";
import { Box, Menu, MenuItem } from "@mui/material";
import { WaitingBackdrop } from "../../../../Base/components/BackDrop";
import { ExportConfirmationDialog } from "../../../DirectBilling/Edit/exports";
import { DeleteConfirmationDialog } from "../../../DirectBilling/common/exports";
import { PaperHeaderButtons } from "./sub-components/PapertHeaderButtons";
import PaperBillCardList from "./PaperEditBillCards";
import BillSummary from "../../../DirectBilling/NewBill/components/sub-components/BillSummary";
import { useBillSummary } from "../../../Base/hooks/useBillSummary";
import { BillingStepper } from "../../../../Base/components/Stepper";
import notificationStore from "../../../../../../stores/Notification/notificationStore";

const PaperEditBillInfo: FC<PaperEditBillInfoProps> = (props) => {
  const {
    // states
    anchorEl,
    openDeleteDialog,
    bills,
    notes,
    updateNote,
    openExportDialog,
    selectedRxId,
    isPrinting,
    isBatchPrinting,
    printProgress,
    todayString,
    testTypeData,
    isExporting,

    // handlers
    setOpenExportDialog,
    setSelectedRxId,
    handleMenuOpen,
    handleMenuClose,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleDruckenBatch,
    handleExport,
    handleExportConfirm,

    // from props
    onRowClick,
    onRemoveAll,
  } = usePaperBillEditInfo(props);

  const { totalRxs, passedValidationRxs, problematicRxs, taxiertRxs, numberOfPayers } =
    useBillSummary({ bills });

  // State for stepper management
  const [activeStep, setActiveStep] = useState(1);
  const [isDocumentGenerated, setIsDocumentGenerated] = useState(false);

  // Handlers for stepper actions
  const handleDruckenClicked = async () => {
    const result = await handleDruckenBatch();
    if (result && result.successCount > 0) {
      setIsDocumentGenerated(true);
      setActiveStep(2);
    }
  };

  const handleExportClicked = async () => {
    // Prevent export if not printed yet
    if (activeStep < 2) {
      notificationStore.showMessage(
        "Bitte drucken Sie zuerst die Belege aus, bevor Sie exportieren.",
        "warning",
      );
      return;
    }
    // Open export dialog - don't increment step yet
    await handleExport();
  };

  const handleExportConfirmClicked = async () => {
    await handleExportConfirm();
    setActiveStep(3);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Backdrop for operations */}
      <WaitingBackdrop
        isWorking={isPrinting || isBatchPrinting || isExporting}
        message={
          isPrinting || isBatchPrinting
            ? "Bitte warten. PDFs werden erstellt..."
            : "Bitte warten. Export läuft..."
        }
        progress={isBatchPrinting ? printProgress : undefined}
      />

      {/* Header Section with Stepper and Controls */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backgroundColor: "#5c758a",
          borderRadius: "0px 0px 10px 10px",
        }}
      >
        {/* Stepper */}
        <Box sx={{ mt: 2 }}>
          <BillingStepper activeStep={activeStep} isPaperBilling={true} />
        </Box>

        {/* Header Buttons */}
        <PaperHeaderButtons
          handleDrucken={handleDruckenClicked}
          handleExport={handleExportClicked}
          isDocumentGenerated={isDocumentGenerated}
        />

        {/* Bill Summary */}
        <Box sx={{ px: 1 }}>
          <BillSummary
            totalRxs={totalRxs}
            passedValidationRxs={passedValidationRxs}
            problematicRxs={problematicRxs}
            taxiertRxs={taxiertRxs}
            numberOfPayers={numberOfPayers}
          />
        </Box>
      </Box>

      {/* Bills Content */}
      <PaperBillCardList
        bills={bills}
        todayString={todayString}
        selectedRxId={selectedRxId}
        notes={notes}
        updateNote={updateNote}
        onRowClick={onRowClick}
        setSelectedRxId={setSelectedRxId}
        handleMenuOpen={handleMenuOpen}
        onRemoveAll={onRemoveAll}
      />

      {/* Export Confirmation Dialog */}
      <ExportConfirmationDialog
        open={openExportDialog}
        onClose={() => {
          setOpenExportDialog(false);
        }}
        onConfirm={() => {
          handleExportConfirmClicked();
        }}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Transaktionsbestätigung löschen"
        content="Dies ist das letzte Element. Möchten Sie die Transaktion wirklich löschen?"
      />

      {/* Context Menu (Kebab Menu) */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleDeleteClick}>Entfernen</MenuItem>
      </Menu>
    </Box>
  );
};

export default PaperEditBillInfo;
